/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BlockedTimeSlot,
  CardState,
  CardStatus,
  MeetingRoomFeature,
  TimeString,
  VirtualCardDeviceType,
  InsightsTileConfig,
  InsightsCountTile,
  InsightsChartTile,
} from "@/types";
import { env } from "./env";
import { enGB, nb } from "date-fns/locale";
import { format, fromZonedTime, toZonedTime } from "date-fns-tz";
import { getDay, parse, startOfWeek } from "date-fns";
import { dateFnsLocalizer } from "react-big-calendar";
import { LayoutItem } from "react-grid-layout/legacy";

export const ADDRESS_REGEX = /\w+(\s\w+){1,5}$/u;
export const DOMAIN_REGEX = /^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
export const NUMERIC_REGEX = /^[0-9+]+$/;
export const MESSAGE_REGEX = /^[\p{L}\p{N}\s!'\-&()/?,."]*$/u;
export const NAME_REGEX = /^[\p{L}\s\-&.']*$/u;
export const NAME_WITH_NUMBER_REGEX = /^[\p{L}\s\-&.0-9]*$/u;
export const COMPANY_REGEX = /^[\p{L}\p{N}\s\-&.()+]*$/u;
export const PHONE_REGEX = /^[+]([0-9]{7,15})$/;
export const ORG_NUMBER_REGEX = /^[0-9]{9}$/;
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const EMOJI_REGEX =
  /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;
// separate non-global instance — g flag makes RegExp.test() stateful (lastIndex)
const EMOJI_REGEX_TEST = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;
export const stripEmojis = (text: string) =>
  text.replace(EMOJI_REGEX, "").replace(/\s{2,}/g, " ");
export const containsEmoji = (text: string) => EMOJI_REGEX_TEST.test(text);

export const CUSTOM_MENU_ITEMS_LIMIT = 6;

// VAT rates (%)
export const SERVICE_VAT_RATE = 25;
export const BASE_VAT_RATE = 15;

export const WELCOME_SUPPORT_EMAIL = "support@welcomeworkdays.com";

export const WELCOME_WEBSITE_URL = (lang: string) =>
  `https://welcomeworkdays.com${lang === "en" ? "/en" : ""}`;

export const WELCOME_TERMS_OF_USE_URL = (lang: string) =>
  `${WELCOME_WEBSITE_URL(lang)}/terms`;

export const WELCOME_PRIVACY_POLICY_URL = (lang: string) =>
  `${WELCOME_WEBSITE_URL(lang)}/privacy`;

const WELCOME_BASE_HELP_URL = "https://help.welcomeworkdays.com";

export const WELCOME_HELP_URL = (language: string) =>
  language === "no"
    ? `${WELCOME_BASE_HELP_URL}/nb`
    : `${WELCOME_BASE_HELP_URL}/${language}`;

export const WELCOME_USER_ADMINISTRATION_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/collections/316566-funksjonsbeskrivelser`;

export const WELCOME_VISITORS_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/collections/1449546-welcome-visit`;

export const WELCOME_ACCESS_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/collections/1449549-welcome-access`;

export const WELCOME_ACCESS_APP_STORE_URL =
  "https://apps.apple.com/no/app/welcome-access/id6739344500?l=nb";

export const WELCOME_ACCESS_GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=eu.sharry.swp.welcome";

export const WELCOME_BOOKING_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/collections/1449553-welcome-booking`;

export const WELCOME_COMMUNICATION_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/collections/778185-welcome-communication`;

export const WELCOME_LOGIN_METHOD_HELP_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/articles/240918-choosing-a-login-method-sso-vs-passwordless-email-sign-on`;

export const WELCOME_FOOD_HELP_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/collections/1449557-welcome-food`;

export const WELCOME_VISIT_ABOUT_URL = (lang: string) =>
  `${WELCOME_HELP_URL(lang)}/articles/252394-about-welcome-visit`;

export const CURR_VERSION = env("VITE_FRONTEND_VERSION");

export const ENVIRONMENT = env("VITE_ENVIRONMENT") as
  "dev" | "prod" | "preview";
const project = env("VITE_FB_PROJECT");
export const REAL_ESTATE = env("VITE_REAL_ESTATE");
if (
  ENVIRONMENT !== "dev" &&
  ENVIRONMENT !== "prod" &&
  ENVIRONMENT !== "preview"
) {
  throw new Error("Invalid environment");
}

export function APP_DOMAIN() {
  const domainMap = {
    dev: `https://portal-${project}.web.app`,
    prod:
      REAL_ESTATE === "production"
        ? `https://app.welcomeworkdays.com`
        : `https://${REAL_ESTATE}.welcomeworkdays.com`,
    preview:
      REAL_ESTATE === "preview"
        ? `https://app.welcomeworkdays.dev`
        : `https://${REAL_ESTATE}.welcomeworkdays.dev`,
  };

  return domainMap[ENVIRONMENT];
}

export const WELCOME_VISIT_KIOSK_URL = (id: string, buildingId: string) => {
  const domainMap = {
    dev:
      window.location.hostname === "localhost"
        ? `${import.meta.env.VITE_VISIT_URL ?? "http://localhost:3000"}?entranceId=${id}&buildingId=${buildingId}`
        : `https://visit-${project}.web.app?entranceId=${id}&buildingId=${buildingId}`,
    prod:
      REAL_ESTATE === "production"
        ? `https://visit.welcomeworkdays.com?entranceId=${id}&buildingId=${buildingId}`
        : `https://visit-${REAL_ESTATE}.welcomeworkdays.com?entranceId=${id}&buildingId=${buildingId}`,
    preview:
      REAL_ESTATE === "preview"
        ? `https://visit.welcomeworkdays.dev?entranceId=${id}&buildingId=${buildingId}`
        : `https://visit-${REAL_ESTATE}.welcomeworkdays.dev?entranceId=${id}&buildingId=${buildingId}`,
  };

  return domainMap[ENVIRONMENT];
};

// One shared guest-food host per env, because Mollie takes a single redirect URL
// (wlcm-food GUEST_FOOD_DOMAINS). Other real estates have no host, so no link.
const GUEST_FOOD_HOSTS: Partial<Record<string, string>> = {
  "develop-aa51ba83e092d486":
    "https://guest-food-develop-aa51ba83e092d486.firebaseapp.com",
  preview: "https://guest-food.welcomeworkdays.dev",
  production: "https://guest-food.welcomeworkdays.com",
};

export const WELCOME_GUEST_FOOD_URL = (buildingId: string) => {
  const host = GUEST_FOOD_HOSTS[REAL_ESTATE];
  return host && `${host}/?buildingId=${encodeURIComponent(buildingId)}`;
};

export const accessCardStatusColorMap = {
  [CardStatus.ORDERED]: "var(--wlcm-color-malling-purple)",
  [CardStatus.CANCELLED]: "var(--wlcm-color-dark-inactive-grey)",
  [CardStatus.COMPLETED]: "var(--wlcm-color-success-green)",
};

export const accessCardStatusColorMapNew = {
  [CardState.AVAILABLE]: "var(--wlcm-color-malling-purple)",
  [CardState.ACTIVE]: "var(--wlcm-color-success-green)",
  [CardState.INACTIVE]: "var(--wlcm-color-warning-orange)",
  [CardState.DEACTIVATED]: "var(--wlcm-color-dark-inactive-grey)",
  [CardState.ACTIVATING]: "var(--wlcm-color-darker-grey)",
  [CardState.DEACTIVATING]: "var(--wlcm-color-darker-grey)",
};

export const accessCardStatusBgColorMapNew = {
  [CardState.AVAILABLE]: "#b48ace1a", // var(--wlcm-color-malling-purple) + 1a
  [CardState.ACTIVE]: "#60a5611a", // var(--wlcm-color-success-green) + 1a
  [CardState.INACTIVE]: "#ef9c0b1a", // var(--wlcm-color-warning-orange) + 1a
  [CardState.DEACTIVATED]: "#7272721a", // var(--wlcm-color-dark-inactive-grey) + 1a
  [CardState.ACTIVATING]: "#7f7f7f1a", // var(--wlcm-color-darker-grey) + 1a
  [CardState.DEACTIVATING]: "#7f7f7f1a", // var(--wlcm-color-darker-grey) + 1a
};

export {
  snackStatusSortMap,
  snackStatusColorMap,
  snackStatusStepMap,
} from "./snackOrderLifecycle";

// Based on EU’s 14 major food allergens, which covers the Codex list and Big 9 (and are required to be disclosed in Norway)
export const ALLERGEN_KEYS = [
  // Main EU allergens
  "CEREALS_CONTAINING_GLUTEN", // Cereals containing gluten (e.g. wheat, rye, barley, oats)
  "CRUSTACEANS", // Crustaceans (e.g. crab, lobster, shrimp)
  "EGGS", // Eggs
  "FISH", // Fish
  "PEANUTS", // Peanuts
  "SOYBEANS", // Soybeans
  "MILK", // Milk
  "TREE_NUTS", // Nuts (e.g. almonds, hazelnuts, walnuts, cashews, pecans, Brazil nuts, pistachios, macadamias)
  "CELERY", // Celery
  "MUSTARD", // Mustard
  "SESAME_SEEDS", // Sesame seeds
  "SULPHUR_DIOXIDE_AND_SULPHITES", // Sulphur dioxide and sulphites
  "LUPIN", // Lupin
  "MOLLUSCS", // Molluscs (e.g. mussels, clams, oysters, squid)

  // Grain sub-allergens
  "WHEAT", // Wheat and wheat-based products
  "RYE", // Rye and rye-based products
  "BARLEY", // Barley and barley-based products
  "OATS", // Oats and oat-based products
  "CORN", // Corn (maize) and corn-based products

  // Crustacean sub-allergens
  "CRAB", // Crab and crab-derived products
  "LOBSTER", // Lobster and lobster-derived products
  "SHRIMP", // Shrimp and prawn products

  // Fish sub-allergens
  "SALMON", // Salmon and salmon-derived products
  "TUNA", // Tuna and tuna-derived products

  // Tree nut sub-allergens
  "ALMONDS", // Almonds and almond-based products
  "HAZELNUTS", // Hazelnuts and hazelnut-based products
  "WALNUTS", // Walnuts and walnut-based products
  "CASHEWS", // Cashews and cashew-based products
  "PECANS", // Pecans and pecan-based products
  "BRAZIL_NUTS", // Brazil nuts and Brazil nut-based products
  "PISTACHIOS", // Pistachios and pistachio-based products
  "MACADAMIA_NUTS", // Macadamia nuts and macadamia-based products

  // Mollusc sub-allergens
  "MUSSELS", // Mussels and mussel-based products
  "CLAMS", // Clams and clam-based products
  "OYSTERS", // Oysters and oyster-based products
  "SQUID", // Squid and squid-based products

  // Other common allergens
  "KIWI", // Kiwi fruit and kiwi-based products
  "STRAWBERRIES", // Strawberries and strawberry-based products
];

export const SUPPORTED_PHONE_COUNTRY_CODES = [
  { code: "us", dialCode: "+1" }, // United States
  { code: "gr", dialCode: "+30" }, // Greece
  { code: "nl", dialCode: "+31" }, // Netherlands
  { code: "be", dialCode: "+32" }, // Belgium
  { code: "fr", dialCode: "+33" }, // France
  { code: "es", dialCode: "+34" }, // Spain
  { code: "hu", dialCode: "+36" }, // Hungary
  { code: "it", dialCode: "+39" }, // Italy
  { code: "ro", dialCode: "+40" }, // Romania
  { code: "ch", dialCode: "+41" }, // Switzerland
  { code: "at", dialCode: "+43" }, // Austria
  { code: "gb", dialCode: "+44" }, // United Kingdom
  { code: "dk", dialCode: "+45" }, // Denmark
  { code: "se", dialCode: "+46" }, // Sweden
  { code: "no", dialCode: "+47" }, // Norway
  { code: "pl", dialCode: "+48" }, // Poland
  { code: "de", dialCode: "+49" }, // Germany
  { code: "br", dialCode: "+55" }, // Brazil
  { code: "ph", dialCode: "+63" }, // Philippines
  { code: "vn", dialCode: "+84" }, // Vietnam
  { code: "in", dialCode: "+91" }, // India
  { code: "ma", dialCode: "+212" }, // Morocco
  { code: "lr", dialCode: "+231" }, // Liberia
  { code: "pt", dialCode: "+351" }, // Portugal
  { code: "lu", dialCode: "+352" }, // Luxembourg
  { code: "ie", dialCode: "+353" }, // Ireland
  { code: "is", dialCode: "+354" }, // Iceland
  { code: "mt", dialCode: "+356" }, // Malta
  { code: "cy", dialCode: "+357" }, // Cyprus
  { code: "fi", dialCode: "+358" }, // Finland
  { code: "bg", dialCode: "+359" }, // Bulgaria
  { code: "lt", dialCode: "+370" }, // Lithuania
  { code: "lv", dialCode: "+371" }, // Latvia
  { code: "ee", dialCode: "+372" }, // Estonia
  { code: "hr", dialCode: "+385" }, // Croatia
  { code: "si", dialCode: "+386" }, // Slovenia
  { code: "cz", dialCode: "+420" }, // Czech Republic
  { code: "sk", dialCode: "+421" }, // Slovakia
  { code: "bo", dialCode: "+591" }, // Bolivia
  { code: "ec", dialCode: "+593" }, // Ecuador
  { code: "kh", dialCode: "+855" }, // Cambodia
];

export const MEETING_ROOM_FEATURES = {
  speaker: {
    icon: "speaker",
  },
  tv: {
    icon: "desktop",
  },
  whiteboard: {
    icon: "draw",
  },
  webcam: {
    icon: "webcam",
  },
  dual_monitors: {
    icon: "tv_displays",
  },
  wireless_screenshare: {
    icon: "cast",
  },
  projector: {
    icon: "projector",
  },
  mic: {
    icon: "mic",
  },
  coffee: {
    icon: "coffee",
  },
  hearing_loop: {
    icon: "hearing_loop",
  },
};

export const MEETING_ROOM_FEATURE_OPTIONS: MeetingRoomFeature[] =
  Object.entries(MEETING_ROOM_FEATURES).map(([key, value]) => ({
    value: key,
    label: key.toUpperCase(),
    icon: value.icon,
  }));

export const DEFAULT_BUILDING_OPENING_HOURS = {
  openingHour: "00:00" as TimeString,
  closingHour: "23:00" as TimeString,
};
export const DEFAULT_BOOKING_SETTINGS = {
  minTime: "00:00" as TimeString,
  maxTime: "23:30" as TimeString,
  minDate: new Date(),
  maxDaysAhead: 30,
  maxDuration: 8,
  maxBookingsPerPerson: 8,
  closedOnDays: [],
  showNotesInput: false,
};
export const DEFAULT_EXTERNAL_BOOKING_SETTINGS = {
  minTime: "08:00" as TimeString,
  maxTime: "17:00" as TimeString,
  closedOnDays: [0, 6],
  blockBookingOn: [] as BlockedTimeSlot[],
};

export const LOCALES_DATE_FNS_MAPPING = {
  en: enGB,
  no: nb,
};
export const DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";
export const OSLO_TIME_ZONE = "Europe/Oslo";

const calendarCustomFormat = (
  date: Date,
  formatString: string,
  options?: object,
) => {
  const zonedDate = toZonedTime(date, OSLO_TIME_ZONE);
  return format(zonedDate, formatString, options);
};
const calendarCustomParse = (
  value: string,
  formatString: string,
  backupDate: Date,
  options?: object,
) => {
  const parsedDate = parse(value, formatString, backupDate, options);
  return fromZonedTime(parsedDate, OSLO_TIME_ZONE);
};

export const CALENDAR_LOCALIZER = dateFnsLocalizer({
  format: calendarCustomFormat,
  parse: calendarCustomParse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: LOCALES_DATE_FNS_MAPPING,
});

export const CALENDAR_FORMATS = {
  timeGutterFormat: "HH:mm", // Format for the time gutter (left side)
  eventTimeRangeFormat: ({ start, end }: any, culture: any, localizer: any) =>
    `${localizer.format(start, "HH:mm", culture)} - ${localizer.format(end, "HH:mm", culture)}`,
  dayRangeHeaderFormat: ({ start, end }: any, culture: any, localizer: any) => {
    const startMonth = localizer.format(start, "LLLL", culture);
    const endMonth = localizer.format(end, "LLLL", culture);

    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = startMonth === endMonth;

    if (sameMonth) {
      return `${localizer.format(start, "d.", culture)}–${localizer.format(end, "d. LLLL", culture)}`;
    }

    if (sameYear) {
      return `${localizer.format(start, "d. LLL", culture)} – ${localizer.format(end, "d. LLL", culture)}`;
    }

    return `${localizer.format(start, "d. LLL yyyy", culture)} – ${localizer.format(end, "d. LLL yyyy", culture)}`;
  },
  dayHeaderFormat: (date: Date, culture: any, localizer: any) => {
    return culture === "en"
      ? localizer.format(date, "EEEE, d. LLLL", culture)
      : localizer.format(date, "EEEE d. LLLL", culture);
  },
  agendaTimeRangeFormat: ({ start, end }: any, culture: any, localizer: any) =>
    `${localizer.format(start, "HH:mm", culture)} - ${localizer.format(end, "HH:mm", culture)}`,
};

export const genericEmailDomains = [
  "aol.com",
  "att.net",
  "comcast.net",
  "facebook.com",
  "gmail.com",
  "gmx.com",
  "googlemail.com",
  "google.com",
  "hotmail.com",
  "hotmail.co.uk",
  "mac.com",
  "me.com",
  "mail.com",
  "msn.com",
  "live.com",
  "sbcglobal.net",
  "verizon.net",
  "yahoo.com",
  "yahoo.co.uk",
  "email.com",
  "fastmail.fm",
  "games.com",
  "gmx.net",
  "hush.com",
  "hushmail.com",
  "icloud.com",
  "iname.com",
  "inbox.com",
  "lavabit.com",
  "love.com",
  "outlook.com",
  "pobox.com",
  "protonmail.ch",
  "protonmail.com",
  "tutanota.de",
  "tutanota.com",
  "tutamail.com",
  "tuta.io",
  "keemail.me",
  "rocketmail.com",
  "safe-mail.net",
  "wow.com",
  "ygm.com",
  "ymail.com",
  "zoho.com",
  "yandex.com",
  "bellsouth.net",
  "charter.net",
  "cox.net",
  "earthlink.net",
  "juno.com",
  "btinternet.com",
  "virginmedia.com",
  "blueyonder.co.uk",
  "freeserve.co.uk",
  "live.co.uk",
  "ntlworld.com",
  "o2.co.uk",
  "orange.net",
  "sky.com",
  "talktalk.co.uk",
  "tiscali.co.uk",
  "virgin.net",
  "wanadoo.co.uk",
  "bt.com",
  "sina.com",
  "sina.cn",
  "qq.com",
  "naver.com",
  "hanmail.net",
  "daum.net",
  "nate.com",
  "yahoo.co.jp",
  "yahoo.co.kr",
  "yahoo.co.id",
  "yahoo.co.in",
  "yahoo.com.sg",
  "yahoo.com.ph",
  "163.com",
  "yeah.net",
  "126.com",
  "21cn.com",
  "aliyun.com",
  "foxmail.com",
  "hotmail.fr",
  "live.fr",
  "laposte.net",
  "yahoo.fr",
  "wanadoo.fr",
  "orange.fr",
  "gmx.fr",
  "sfr.fr",
  "neuf.fr",
  "free.fr",
  "gmx.de",
  "hotmail.de",
  "live.de",
  "online.de",
  "t-online.de",
  "web.de",
  "yahoo.de",
  "libero.it",
  "virgilio.it",
  "hotmail.it",
  "aol.it",
  "tiscali.it",
  "alice.it",
  "live.it",
  "yahoo.it",
  "email.it",
  "tin.it",
  "poste.it",
  "teletu.it",
  "mail.ru",
  "rambler.ru",
  "yandex.ru",
  "ya.ru",
  "list.ru",
  "hotmail.be",
  "live.be",
  "skynet.be",
  "voo.be",
  "tvcablenet.be",
  "telenet.be",
  "hotmail.com.ar",
  "live.com.ar",
  "yahoo.com.ar",
  "fibertel.com.ar",
  "speedy.com.ar",
  "arnet.com.ar",
  "yahoo.com.mx",
  "live.com.mx",
  "hotmail.es",
  "hotmail.com.mx",
  "prodigy.net.mx",
  "yahoo.ca",
  "hotmail.ca",
  "bell.net",
  "shaw.ca",
  "sympatico.ca",
  "rogers.com",
  "yahoo.com.br",
  "hotmail.com.br",
  "outlook.com.br",
  "uol.com.br",
  "bol.com.br",
  "terra.com.br",
  "ig.com.br",
  "itelefonica.com.br",
  "r7.com",
  "zipmail.com.br",
  "globo.com",
  "globomail.com",
  "oi.com.br",
];

export const VIRTUAL_CARD_ICONS: Record<VirtualCardDeviceType, string> = {
  [VirtualCardDeviceType.MOBILE]: "phone_android",
  [VirtualCardDeviceType.WATCH]: "fitness_tracker",
};

export const DOM_PURIFY_CONFIG = {
  USE_PROFILES: { html: true },
  ALLOWED_TAGS: [
    "div",
    "img",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "br",
    "span",
  ],
  ALLOWED_ATTR: ["src", "alt", "style", "class"],
  ALLOWED_URI_REGEXP: /^(blob:|https?:|\/|\.{0,2}\/|\/\/)/i,
};

export const CARD_ANIMATION_VARIANTS = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.2, ease: "easeInOut" },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -100 : 100,
    opacity: 0,
    transition: { duration: 0.3, ease: "easeInOut" },
  }),
};

export const emptyOrgNrsMaps = {
  usersOrgNrMap: new Map<string, string>(),
  companiesOrgNrMap: new Map<string, string>(),
  subdivisionsOrgNrMap: new Map<string, string>(),
};

export const NO_VALUE_MARKER = "__NO_VALUE_MARKER__";
export const HAS_VALUE_MARKER = "__HAS_VALUE_MARKER__";

export const FOOD_INSIGHTS_DEFAULT_TILES: InsightsTileConfig[] = [
  {
    id: "default-1",
    kind: "chart",
    title: "Lunsjer per dag",
    chartType: "bar",
    groupBy: "date",
    valueKey: "count",
    scope: "all",
  } satisfies InsightsChartTile,
  {
    id: "default-2",
    kind: "chart",
    title: "Salg per dag",
    chartType: "line",
    groupBy: "date",
    valueKey: "totalAmount",
    scope: "all",
  } satisfies InsightsChartTile,
  {
    id: "default-3",
    kind: "count",
    label: "Totalt salg",
    summaryKey: "totalSales",
    formatter: "currency",
  } satisfies InsightsCountTile,
  {
    id: "default-4",
    kind: "count",
    label: "Betalt fra kreditt",
    summaryKey: "totalPaidFromCredit",
    formatter: "currency",
  } satisfies InsightsCountTile,
  {
    id: "default-5",
    kind: "count",
    label: "Betalt fra saldo",
    summaryKey: "totalPaidFromBalance",
    formatter: "currency",
  } satisfies InsightsCountTile,
  {
    id: "default-6",
    kind: "count",
    label: "Subsidiert av arbeidsgiver",
    summaryKey: "totalCompanySubsidy",
    formatter: "currency",
  } satisfies InsightsCountTile,
  {
    id: "default-7",
    kind: "chart",
    title: "Salg per selskap",
    chartType: "pie",
    groupBy: "company",
    valueKey: "totalAmount",
  } satisfies InsightsChartTile,
];

export const FOOD_INSIGHTS_DEFAULT_LAYOUTS: Record<string, LayoutItem[]> = {
  lg: [
    { i: "default-1", x: 0, y: 0, w: 6, h: 3, minW: 3, minH: 2 },
    { i: "default-2", x: 6, y: 3, w: 6, h: 3, minW: 3, minH: 2 },
    { i: "default-3", x: 6, y: 0, w: 6, h: 2, minW: 2, minH: 1 },
    { i: "default-4", x: 6, y: 2, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-5", x: 8, y: 2, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-6", x: 10, y: 2, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-7", x: 0, y: 6, w: 6, h: 3, minW: 3, minH: 2 },
  ],
  md: [
    { i: "default-1", x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 2 },
    { i: "default-2", x: 0, y: 4, w: 5, h: 3, minW: 3, minH: 2 },
    { i: "default-3", x: 6, y: 0, w: 4, h: 1, minW: 2, minH: 1 },
    { i: "default-4", x: 6, y: 1, w: 4, h: 1, minW: 2, minH: 1 },
    { i: "default-5", x: 6, y: 2, w: 4, h: 1, minW: 2, minH: 1 },
    { i: "default-6", x: 6, y: 3, w: 4, h: 1, minW: 2, minH: 1 },
    { i: "default-7", x: 5, y: 4, w: 5, h: 3, minW: 3, minH: 2 },
  ],
  sm: [
    { i: "default-1", x: 0, y: 0, w: 6, h: 3, minW: 3, minH: 2 },
    { i: "default-2", x: 0, y: 5, w: 6, h: 3, minW: 3, minH: 2 },
    { i: "default-3", x: 0, y: 3, w: 3, h: 1, minW: 2, minH: 1 },
    { i: "default-4", x: 3, y: 3, w: 3, h: 1, minW: 2, minH: 1 },
    { i: "default-5", x: 0, y: 4, w: 3, h: 1, minW: 2, minH: 1 },
    { i: "default-6", x: 3, y: 4, w: 3, h: 1, minW: 2, minH: 1 },
    { i: "default-7", x: 0, y: 8, w: 6, h: 3, minW: 3, minH: 2 },
  ],
  xs: [
    { i: "default-1", x: 0, y: 0, w: 4, h: 3, minW: 3, minH: 2 },
    { i: "default-2", x: 0, y: 5, w: 4, h: 3, minW: 3, minH: 2 },
    { i: "default-3", x: 0, y: 3, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-4", x: 2, y: 3, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-5", x: 0, y: 4, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-6", x: 2, y: 4, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-7", x: 0, y: 8, w: 4, h: 3, minW: 3, minH: 2 },
  ],
  xxs: [
    { i: "default-1", x: 0, y: 0, w: 2, h: 3, minW: 3, minH: 2 },
    { i: "default-2", x: 0, y: 7, w: 2, h: 3, minW: 3, minH: 2 },
    { i: "default-3", x: 0, y: 3, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-4", x: 0, y: 4, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-5", x: 0, y: 5, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-6", x: 0, y: 6, w: 2, h: 1, minW: 2, minH: 1 },
    { i: "default-7", x: 0, y: 10, w: 2, h: 3, minW: 3, minH: 2 },
  ],
};

export const EXCEL_NOK_FORMAT = '"kr " #,##0.00';
export const EXCEL_PERCENTAGE_FORMAT = '0.00"%"';
